import type { Plant, PlantKiln } from '../../types/entities';

/** Der reine Datumsanteil eines API-Werts (`2025-06-30T00:00:00+02:00` → `2025-06-30`).
 *
 * `activeFrom`/`activeUntil` sind fachlich Tagesangaben, die API liefert sie als ISO-Zeitstempel
 * mit Zonenversatz. Verglichen wird deshalb der Datumsteil als Zeichenkette (ISO-Datum sortiert
 * lexikografisch korrekt) statt ueber `Date`-Objekte -- so kann keine Zeitzone einen Ofen einen
 * Tag zu frueh oder zu spaet stilllegen. Exportiert, weil `isPlantActive`/`formatIsoDateDe`
 * (Phase 42) dieselbe Regel fuer `Plant.activeUntil` brauchen. */
export function toIsoDate(value: string): string {
  return value.slice(0, 10);
}

/** Heute als `YYYY-MM-DD` in lokaler Zeit. */
export function todayIsoDate(now: Date = new Date()): string {
  const month = `${now.getMonth() + 1}`.padStart(2, '0');
  const day = `${now.getDate()}`.padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
}

/**
 * Ist der Ofen zum Stichtag in Betrieb?
 *
 * Uebernimmt woertlich die Regel, nach der das Backend die Oefen einer Meldeperiode auswaehlt
 * (`PlantKilnsInitializer.php:54-55`), angewandt auf den Zeitraum "heute":
 *
 *   aktiv := (activeFrom === null || activeFrom <= stichtag)
 *         && (activeUntil === null || activeUntil >= stichtag)
 *
 * Beide Grenzen sind einschliessend: am Tag `activeUntil` laeuft der Ofen noch. Ein Ofen mit einem
 * Stilllegungsdatum in der Zukunft ist heute also aktiv, einer mit einem Datum in der
 * Vergangenheit nicht -- deshalb ist das Feld ein Datum und kein Ja/Nein-Schalter.
 */
export function isKilnActive(
  kiln: Pick<PlantKiln, 'activeFrom' | 'activeUntil'>,
  onDate: string = todayIsoDate()
): boolean {
  const startsInTime = !kiln.activeFrom || toIsoDate(kiln.activeFrom) <= onDate;
  const stillRunning = !kiln.activeUntil || toIsoDate(kiln.activeUntil) >= onDate;
  return startsInTime && stillRunning;
}

/** Nur die zum Stichtag laufenden Oefen. */
export function activeKilns<T extends Pick<PlantKiln, 'activeFrom' | 'activeUntil'>>(
  kilns: readonly T[] | undefined,
  onDate: string = todayIsoDate()
): T[] {
  return (kilns ?? []).filter((kiln) => isKilnActive(kiln, onDate));
}

/**
 * Ist das Werk zum Stichtag in Betrieb?
 *
 * D-05: dieselbe einschliessende Datumsregel wie `isKilnActive` -- kein Datum oder ein Datum in
 * der Zukunft/am Stichtag selbst heisst aktiv. `activeUntil` ist ein Datum, kein Ja/Nein-Schalter.
 */
export function isPlantActive(
  plant: Pick<Plant, 'activeUntil'>,
  onDate: string = todayIsoDate()
): boolean {
  return !plant.activeUntil || toIsoDate(plant.activeUntil) >= onDate;
}

/**
 * D-11: Ein Ofen gilt zum Stichtag nur als aktiv, wenn er selbst UND sein Werk aktiv sind. Rein
 * abgeleitet -- das Werksdatum wird nirgends in `PlantKiln.activeUntil` geschrieben, sonst weiss
 * beim Aufheben der Stilllegung niemand mehr, welche Oefen vorher ein eigenes Datum hatten.
 */
export function effectiveActiveKilns(
  plant: Pick<Plant, 'activeUntil' | 'kilns'>,
  onDate: string = todayIsoDate()
): PlantKiln[] {
  if (!isPlantActive(plant, onDate)) {
    return [];
  }
  return activeKilns(plant.kilns, onDate);
}

/** `2026-06-30T00:00:00+02:00` bzw. `2026-06-30` → `30.06.2026`. Reine Stringzerlegung auf dem
 * bereits zeitzonensicheren `toIsoDate`-Ergebnis, keine `Date`-Objekte (gleiche Begruendung wie
 * dort: eine lokale Umwandlung koennte am Datum einen Tag verschieben). */
export function formatIsoDateDe(value: string): string {
  const [year, month, day] = toIsoDate(value).split('-');
  return `${day}.${month}.${year}`;
}

/**
 * D-12: Kennzeichnungstext fuer den Stammdatenbaum -- `null` ohne Stilllegungsdatum,
 * `"stillgelegt TT.MM.JJJJ"` fuer ein vergangenes und `"bis TT.MM.JJJJ"` fuer ein heutiges oder
 * kuenftiges Datum (Sketch 027, Variante D, `treeMark`).
 */
export function plantClosureLabel(
  plant: Pick<Plant, 'activeUntil'>,
  onDate: string = todayIsoDate()
): string | null {
  if (!plant.activeUntil) {
    return null;
  }
  const formatted = formatIsoDateDe(plant.activeUntil);
  return isPlantActive(plant, onDate) ? `bis ${formatted}` : `stillgelegt ${formatted}`;
}
